import { createTheme, PaletteMode } from '@mui/material/styles';

// Create a theme with light or dark mode
export const createAppTheme = (mode: PaletteMode) => createTheme({
  palette: {
    mode,
    primary: {
      main: mode === 'dark' ? '#5CBBFF' : '#0A2463', // Lighter blue in dark mode for better contrast
      light: mode === 'dark' ? '#81D4FF' : '#3E5C95',
      dark: mode === 'dark' ? '#3388CC' : '#051333',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: mode === 'dark' ? '#64B5F6' : '#1E88E5', // Lighter blue in dark mode
      light: mode === 'dark' ? '#90CAF9' : '#5AB0FF',
      dark: mode === 'dark' ? '#42A5F5' : '#0D5DB3',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#D32F2F',
    },
    warning: {
      main: '#FFA000',
    },
    info: {
      main: mode === 'dark' ? '#64B5F6' : '#1976D2',
    },
    success: {
      main: '#388E3C',
    },
    background: {
      default: mode === 'light' ? '#FAFAFA' : '#121212',
      paper: mode === 'light' ? '#FFFFFF' : '#1E1E1E',
    },
    text: {
      primary: mode === 'light' ? '#333333' : '#FFFFFF',
      secondary: mode === 'light' ? '#5F5F5F' : '#AAAAAA',
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body1: {
      fontFamily: '"Open Sans", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: '"Open Sans", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '0.75rem 1.5rem',
        },
      },
    },
    // Adjust Card and Paper components for dark mode
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
        }
      }
    },
    // Improve dark mode contrast for links and text
    MuiLink: {
      styleOverrides: {
        root: {
          color: mode === 'dark' ? '#81D4FF' : '#0A2463',
        }
      }
    },
    // Fix for Typography components - use root and variant specific overrides instead
    MuiTypography: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          // Style for Typography with color="primary"
          ...(ownerState.color === 'primary' && {
            color: mode === 'dark' ? '#81D4FF' : '#0A2463',
          }),
          // Style for Typography with color="secondary"
          ...(ownerState.color === 'secondary' && {
            color: mode === 'dark' ? '#90CAF9' : '#1E88E5',
          }),
        }),
      }
    },
  },
});

// Export default light theme for backward compatibility
const theme = createAppTheme('light');
export default theme;